import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// Bìa album dạng DẢI NGANG — dùng chung cho màn nhân sự và màn khách (CEO chốt 27/08/2026,
// kèm ảnh mẫu chụp từ dự án PHOTO: bìa ngang bo góc, tên album IN HOA ở trung tâm, nhãn studio
// nhỏ ngay dưới, nút "Chỉnh sửa ảnh bìa" góc trên-phải).
//
// Vì sao KHÔNG tự lấy URL ảnh ở đây: hai màn lấy đường dẫn ảnh bằng hai đường khác nhau
// (nhân sự dùng useGalleryVersionURLs, khách dùng usePublicGalleryVersionURLs với token chia sẻ).
// Nhận `imageUrl` làm tham số thì một khối dùng được cả hai, không phải nhân đôi giao diện.
//
// ⚠️ Bản trước là bìa PHỦ TRỌN MÀN (min-h-[100dvh]). CEO bỏ vì tỉ lệ chạy theo cửa sổ người xem
// nên trên cửa sổ vuông thì bìa cũng vuông. Nay tỉ lệ do MÌNH quyết: 5:2 từ md, 3:2 ở điện thoại
// (5:2 trên màn 375px chỉ cao 150px, dẹt như một vạch kẻ).

/** Chữ kem trên nền ảnh tối — cùng vai trò `--color-brand-ink` của PHOTO. */
const COVER_INK = "#F9F7F1";

/**
 * Ba cỡ bìa, lấy đúng từ bản xem thử CEO duyệt 26/09/2026:
 *  - `hero` — bìa to 5:2 cho màn khách mở album (không đổi từ 27/08).
 *  - `work` — dải 168px trên màn Kho ảnh của nhân sự (`.cover.work`). Bìa 5:2
 *    trên màn làm việc rộng 1.500px cao tới 600px: người ta phải cuộn qua nửa
 *    màn ảnh bìa mới tới được ảnh cần làm.
 *  - `thin` — dải 96px trên màn Sửa hình (`.cover.thin`): chỉ để biết đang mở
 *    khách nào, không phải để ngắm.
 *  - `tall` — bìa cao kiểu Wanimal cho màn Sửa hình và Kho ảnh (bản xem thử
 *    27/09/2026, CEO chốt 2a — thay cả `work` lẫn `thin` ở hai màn đó): cao
 *    bằng 1/3 bề ngang, không dưới 200px, dừng ở 520px khi màn rất rộng. Số
 *    phải khớp `coverSize.ts` (chọn cỡ ảnh vừa đủ nét theo chiều cao này).
 */
export type AlbumCoverSize = "hero" | "work" | "thin" | "tall";

/**
 * Cách đặt ảnh vào bìa:
 *  - `cover` — ảnh phủ kín, cắt phần thừa (mặc định, như cũ).
 *  - `ambient` — ảnh đứng TRỌN giữa bìa, hai bên là chính ảnh đó phóng to làm
 *    mờ. Cho bìa lấy ảnh của khách (thường là ảnh DỌC): phủ kín một tấm dọc
 *    vào khung 3:1 là cắt ra một dải ngang qua cổ, mất mặt khách; và phải
 *    phóng tấm ảnh cho kín bề ngang màn nên vỡ hạt. Kiểu này ảnh thật chỉ cao
 *    đúng bằng bìa ⇒ nét hơn với cùng một tệp. Lớp mờ dùng lại đúng đường ảnh
 *    đó nên trình duyệt chỉ tải MỘT lần.
 */
export type AlbumCoverFit = "cover" | "ambient";

const SIZE: Record<
  AlbumCoverSize,
  { box: string; mid: string; title: string; studio: string }
> = {
  hero: {
    box: "aspect-[3/2] rounded-xl md:aspect-[5/2]",
    mid: "gap-2 pb-5 md:pb-8",
    title: "max-w-4xl text-xl tracking-wide md:text-4xl",
    studio: "text-[10px] md:text-xs",
  },
  work: {
    box: "h-[168px] rounded-xl",
    mid: "gap-[7px] pb-4",
    title: "text-2xl tracking-[0.04em] leading-[1.15] [text-shadow:0_2px_12px_rgba(0,0,0,.5)]",
    studio: "text-[10.5px] tracking-[0.12em] px-[9px]",
  },
  thin: {
    box: "h-24 rounded-[10px]",
    mid: "gap-[5px] pb-3",
    title: "text-[19px] tracking-[0.04em] leading-[1.15] [text-shadow:0_2px_12px_rgba(0,0,0,.5)]",
    studio: "text-[10.5px] tracking-[0.12em] px-[9px]",
  },
  // `.cover.cao` của bản xem thử 27/09. Điện thoại: bìa kẹp ở 200px mà tên
  // khách kèm mã đơn dài ~27 ký tự — chữ 38px sẽ vỡ ba dòng, nên nhỏ lại.
  tall: {
    box: "aspect-[3/1] min-h-[200px] max-h-[520px] rounded-xl",
    mid: "gap-2.5 pb-5 md:pb-[30px]",
    title:
      "text-2xl tracking-[0.05em] leading-[1.15] md:text-[38px] [text-shadow:0_2px_12px_rgba(0,0,0,.5)]",
    studio: "text-[11.5px] tracking-[0.12em] px-[11px] py-[3px]",
  },
};

export function AlbumCoverBanner({
  imageUrl,
  title,
  studioLabel = "Gạo Nâu Profile Studio",
  actions,
  size = "hero",
  fit = "cover",
  onImageError,
}: {
  imageUrl: string | undefined;
  title: string;
  studioLabel?: string;
  actions?: ReactNode;
  size?: AlbumCoverSize;
  fit?: AlbumCoverFit;
  /** Ảnh bìa hỏng (bản thu nhỏ chưa sinh, đường ký hết hạn…) — nơi dùng tự chọn ảnh thay. */
  onImageError?: () => void;
}) {
  const c = SIZE[size];
  return (
    <section
      data-testid="album-cover"
      data-size={size}
      data-fit={fit}
      className={cn("relative isolate w-full overflow-hidden bg-[#3d3128]", c.box)}
    >
      {imageUrl && fit === "ambient" ? (
        <>
          {/* Phóng 110% để mép mờ không lộ viền trong suốt quanh khung. */}
          <img
            src={imageUrl}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl brightness-75"
          />
          <img
            src={imageUrl}
            alt={`Ảnh bìa ${title}`}
            className="absolute inset-0 h-full w-full object-contain"
            {...(onImageError ? { onError: onImageError } : {})}
          />
        </>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={`Ảnh bìa ${title}`}
          className="absolute inset-0 h-full w-full object-cover"
          {...(onImageError ? { onError: onImageError } : {})}
        />
      ) : null}
      {/* Gradient từ đáy lên: chỉ tối chỗ có chữ, phần ảnh phía trên giữ nguyên độ sáng. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10"
      />

      {actions ? <div className="absolute right-3 top-3 z-10">{actions}</div> : null}

      <div
        className={cn("absolute inset-x-0 bottom-0 flex flex-col items-center px-4 text-center", c.mid)}
        style={{ color: COVER_INK }}
      >
        {/* IN HOA bằng CSS chứ không đụng dữ liệu — tên album trong CSDL giữ nguyên chữ gốc. */}
        <h1
          className={cn("font-semibold uppercase leading-tight break-words drop-shadow-lg", c.title)}
        >
          {title}
        </h1>
        <p
          className={cn(
            "rounded bg-white/85 px-2 py-0.5 font-medium uppercase tracking-wider text-[#231F20]",
            c.studio,
          )}
        >
          {studioLabel}
        </p>
      </div>
    </section>
  );
}
